import { version } from '@/../package.json'

export interface PlatformInfo {
  version: string
  system: string
  beWrapped: boolean
  engine: string
  supporter: string
  shell: string
  shellVs: string
  networkType: string
  systemVs: string
  platform: string
  engineVs: string
  supporterVs: string
  webp: boolean
}

const platformInfo: PlatformInfo = {
  version: '', // 应用版本
  system: '', // 系统
  beWrapped: false, // 是否被包裹
  engine: '', // 内核
  supporter: '', // 载体
  shell: '', // 外壳
  shellVs: '', // 外壳版本
  networkType: '', // 联网方式
  systemVs: '', // 系统版本
  platform: '', // 平台
  engineVs: '', // 内核版本
  supporterVs: '', // 载体版本
  webp: true // 是否支持webp图片显示
}

const navigator = window.navigator
const ua = navigator.userAgent.toLowerCase()

const testUa = (regexp: any) => regexp.test(ua)
const testVs = (regexp: any) => {
  let res = ua.match(regexp) || ''
  res = res
    .toString()
    .replace(/[^0-9|_.]/g, '')
    .replace(/_/g, '.')
  const index = res.indexOf('.')
  if (index === 0) res = res.slice(index + 1)
  return res
}

/**
 * 获取平台信息
 * @return {PlatformInfo} 平台信息
 */
export function getPlatformInfo(): PlatformInfo {
  platformInfo.version = version
  platformInfo.system = getSystem()
  platformInfo.beWrapped = getBeWrapped()
  platformInfo.networkType = getNetworkType()
  platformInfo.systemVs = getSystemVs(platformInfo.system)
  platformInfo.platform = getPlatform(platformInfo.system)
  Object.assign(platformInfo, getEngineSupporter(), getShell())
  platformInfo.engineVs = getEngineVs(platformInfo.engine)
  platformInfo.supporterVs = getSupporterVs(platformInfo.supporter)
  platformInfo.webp = getWebp()

  return platformInfo
}

/**
 * 获取网络类型
 * @return {string} 网络类型
 */
function getNetworkType(): string {
  let type = ((navigator as any).connection?.type ||
    (navigator as any).connection?.effectiveType) as string
  if (type) type = type.toUpperCase()
  else type = 'unknow'
  return type
}

/**
 * 获取系统
 * @return {string} 系统
 */
function getSystem(): string {
  let system = 'unknow'
  if (testUa(/windows|win32|win64|wow32|wow64/g)) system = 'Windows'
  else if (testUa(/macintosh|macintel/g)) system = 'MacOS'
  else if (testUa(/x11/g)) system = 'Linux'
  else if (testUa(/android|adr/g)) system = 'Android'
  else if (testUa(/openharmony/g)) system = 'HarmonyOS'
  else if (testUa(/ios|iphone|ipod|iwatch/g)) system = 'iOS'
  else if (testUa(/ipad/g)) system = 'iPadOS'
  return system
}

/**
 * 获取是否被包裹
 * @return {boolean} 是否被包裹
 */
function getBeWrapped(): boolean {
  return !(window.self === window.top)
}

/**
 * 获取系统版本
 * @param {string} system 系统
 * @return {string} 系统版本
 */
function getSystemVs(system: string): string {
  let systemVs = 'unknow'
  if (system === 'Windows') {
    if (testUa(/windows nt 5.0|windows 2000/g)) systemVs = '2000'
    else if (testUa(/windows nt 5.1|windows xp/g)) systemVs = 'xp'
    else if (testUa(/windows nt 5.2|windows 2003/g)) systemVs = '2003'
    else if (testUa(/windows nt 6.0|windows vista/g)) systemVs = 'vista'
    else if (testUa(/windows nt 6.1|windows 7/g)) systemVs = '7'
    else if (testUa(/windows nt 6.2|windows 8/g)) systemVs = '8'
    else if (testUa(/windows nt 6.3|windows 8.1/g)) systemVs = '8.1'
    else if (testUa(/windows nt 10.0|windows 10/g)) systemVs = '10'
  } else if (system === 'MacOS') {
    systemVs = testVs(/os x [\d._]+/g)
  } else if (system === 'Android') {
    systemVs = testVs(/android [\d._]+/g)
  } else if (system === 'HarmonyOS') {
    systemVs = testVs(/openharmony [\d._]+/g)
  } else if (system === 'iOS' || system === 'iPadOS') {
    systemVs = testVs(/os [\d._]+/g)
  }
  return systemVs
}

/**
 * 获取平台
 * @param {string} system 系统
 * @return {string} 平台
 */
function getPlatform(system: string): string {
  let platform = 'unknow'
  if (['Windows', 'MacOS', 'Linux'].indexOf(system) > -1) platform = 'Desktop'
  else if (testUa(/tablet/g) || testUa(/ipad/g)) platform = 'Pad'
  else if (['Android', 'iOS', 'HarmonyOS'].indexOf(system) > -1 || testUa(/mobile/g)) platform = 'Mobile'
  return platform
}

/**
 * 获取引擎与支持者
 * @return {object} 引擎与支持者
 */
function getEngineSupporter(): { engine: string; supporter: string } {
  let engine = 'unknow'
  let supporter = 'unknow'
  if (testUa(/applewebkit/g)) {
    engine = 'Webkit'
    if (testUa(/edge/g)) supporter = 'Edge'
    else if (testUa(/opr/g)) supporter = 'Opera'
    else if (testUa(/chrome/g)) supporter = 'Chrome'
    else if (testUa(/safari/g)) supporter = 'Safari'
  } else if (testUa(/gecko/g) && testUa(/firefox/g)) {
    engine = 'Gecko'
    supporter = 'Firefox'
  } else if (testUa(/presto/g)) {
    engine = 'Presto'
    supporter = 'Opera'
  } else if (testUa(/trident|compatible|msie/g)) {
    engine = 'Trident'
    supporter = 'IExplore'
  }
  return { engine: engine, supporter: supporter }
}

/**
 * 获取引擎版本
 * @param {string} engine 引擎
 * @return {string} 引擎版本
 */
function getEngineVs(engine: string): string {
  let engineVs = 'unknow'
  if (engine === 'Webkit') engineVs = testVs(/applewebkit\/[\d._]+/g)
  else if (engine === 'Gecko') engineVs = testVs(/gecko\/[\d._]+/g)
  else if (engine === 'Presto') engineVs = testVs(/presto\/[\d._]+/g)
  else if (engine === 'Trident') engineVs = testVs(/trident\/[\d._]+/g)
  return engineVs
}

/**
 * 获取支持者版本
 * @param {string} supporter 支持者
 * @return {string} 支持者版本
 */
function getSupporterVs(supporter: string): string {
  let supporterVs = 'unknow'
  if (supporter === 'Chrome') supporterVs = testVs(/chrome\/[\d._]+/g)
  else if (supporter === 'Safari') supporterVs = testVs(/version\/[\d._]+/g)
  else if (supporter === 'Firefox') supporterVs = testVs(/firefox\/[\d._]+/g)
  else if (supporter === 'Opera') supporterVs = testVs(/opr\/[\d._]+/g)
  else if (supporter === 'IExplore') supporterVs = testVs(/(msie [\d._]+)|(rv:[\d._]+)/g)
  else if (supporter === 'Edge') supporterVs = testVs(/edge\/[\d._]+/g)
  return supporterVs
}

/**
 * 获取外壳与版本
 * @return {object} 外壳与版本
 */
function getShell(): { shell: string; shellVs: string } {
  let shell = 'none'
  let shellVs = 'unknow'
  if (testUa(/miniprogram/g)) {
    // miniprogram 只能表示是一个小程序，目前微信小程序和支付宝小程序均有miniprogram
    // 微信小程序模拟器：Mozilla/5.0 (iPhone; CPU iPhone OS 11_0 like Mac OS X) AppleWebKit/604.1.38 (KHTML, like Gecko) Version/11.0 Mobile/15A372 Safari/604.1 wechatdevtools/1.06.2210310 MicroMessenger/8.0.5 Language/zh_CN webview/17383_11 webdebugger miniprogramhtmlwebview miniProgram port/44874
    // 支付宝小程序模拟器：Mozilla/5.0 (Android 9.0; LIO-L29) AppleWebKit/603.1.30 (KHTML, like Gecko) Mobile/15E148 ChannelId(0) LyraVM Nebula  AlipayDefined(nt:WIFI,ws:360|0|3.0) AliApp(AP/10.2.60) AlipayClient/10.2.60 Language/zh-Hans AlipayIDE MiniProgram
    if (testUa(/MicroMessenger/i)) {
      shell = 'MP-WEIXIN'
    } else if (testUa(/AliApp/i)) {
      shell = 'MP-ALIPAY'
    } else {
      shell = 'MP'
    }
    // 小程序的基础库版本，实际依赖主程序的版本，即微信或支付宝的版本
    shellVs = testVs(/miniprogram\/[\d._]+/g) || testVs(/micromessenger\/[\d._]+/g)
  } else if (testUa(/micromessenger/g)) {
    shell = 'Weixin'
    shellVs = testVs(/micromessenger\/[\d._]+/g)
  } else if (testUa(/qqbrowser/g)) {
    shell = 'QQ'
    shellVs = testVs(/qqbrowser\/[\d._]+/g)
  } else if (testUa(/ucbrowser/g)) {
    shell = 'UC'
    shellVs = testVs(/ucbrowser\/[\d._]+/g)
  } else if (testUa(/qihu 360se/g)) {
    shell = '360'
  } else if (testUa(/2345explorer/g)) {
    shell = '2345'
    shellVs = testVs(/2345explorer\/[\d._]+/g)
  } else if (testUa(/metasr/g)) {
    shell = 'Sougou'
  } else if (testUa(/lbbrowser/g)) {
    shell = 'Liebao'
  } else if (testUa(/maxthon/g)) {
    shell = 'Maxthon'
    shellVs = testVs(/maxthon\/[\d._]+/g)
  } else if (testUa(/ToutiaoMicroApp/i) || testUa(/BytedanceWebview/i)) {
    shell = 'MP-TOUTIAO'
    shellVs = testVs(/ToutiaoMicroApp\/[\d._]+/g) || testVs(/BytedanceWebview\/[\d._]+/g)
  }
  return { shell: shell, shellVs: shell === 'none' ? 'none' : shellVs }
}

/**
 * 获取是否支持webp显示
 * @return {boolean} 是否支持webp显示
 */
function getWebp(): boolean {
  try {
    return (
      document.createElement('canvas').toDataURL('image/webp', 0.5).indexOf('data:image/webp') === 0
    )
  } catch (err) {
    return false
  }
}
